import { useCallback, useEffect, useState } from 'react'

import IconPlus from '@/assets/icons/icon-plus.svg'
import IconWarning from '@/assets/icons/icon-warning.svg'
import { Page } from '@/components/layout/page/page.tsx'
import { Button } from '@/components/ui/button.tsx'
import { SearchInput } from '@/components/ui/search-input.tsx'
import { CreateListDialog } from '@/modules/tasks/components/create-list-dialog'
import { TaskEditorDialog } from '@/modules/tasks/components/create-task-dialog/task-editor-dialog'
import { DeleteTaskDialog } from '@/modules/tasks/components/delete-task-dialog'
import { TaskListTable } from '@/modules/tasks/components/task-list-table'
import type { NavigationEntry } from '@/modules/tasks/components/task-navigation'
import {
  TaskNavigation,
  useNavigationEntries,
} from '@/modules/tasks/components/task-navigation'
import { TaskPageSkeleton } from '@/modules/tasks/components/task-page-skeleton'
import { TaskSchedulerDialog } from '@/modules/tasks/components/task-scheduler-dialog/task-scheduler-dialog.tsx'
import { TaskSchedulerTable } from '@/modules/tasks/components/task-scheduler-table.tsx'
import { TaskTable } from '@/modules/tasks/components/task-table'
import { useUpdateTask } from '@/modules/tasks/query/task-query'
import { useTaskQuery } from '@/modules/tasks/query/task-query-context'
import type { RepeatableTask } from '@/modules/tasks/service/repeatable-task-service.ts'
import { TrackedFeatures, trackFeatureUsage } from '@/utils/feature-engagement'

export const TaskPage = () => {
  const { isFetching, tasks, repeatableTasks, lists } = useTaskQuery()
  const entries = useNavigationEntries(tasks)
  const [isCreateTaskOpen, setCreateTaskOpen] = useState(false)
  const [isCreateSchedulerOpen, setCreateSchedulerOpen] = useState(false)
  const [isCreateListOpen, setCreateListOpen] = useState(false)

  const updateTask = useUpdateTask()

  const handleToggleCompleted = useCallback(
    (task: NavigationEntry, completed: boolean) => {
      updateTask.mutate({ id: task.id, completed })
    },
    [updateTask],
  )

  const [taskToEdit, setTaskToEdit] = useState<NavigationEntry | undefined>()
  const [schedulerToEdit, setSchedulerToEdit] = useState<
    RepeatableTask | undefined
  >()

  const handleShowEditScheduler = useCallback((scheduler: RepeatableTask) => {
    setSchedulerToEdit(scheduler)
    setCreateSchedulerOpen(true)
  }, [])

  const handleShowEdit = useCallback((task: NavigationEntry) => {
    setCreateTaskOpen(true)
    setTaskToEdit(task)
  }, [])

  const [taskToDelete, setTaskToDelete] = useState<
    NavigationEntry | undefined
  >()
  const [showDelete, setShowDelete] = useState(false)

  const handleShowDelete = useCallback((task: NavigationEntry) => {
    setTaskToDelete(task)
    setShowDelete(true)
  }, [])

  const handleDeleteScheduler = useCallback((scheduler: RepeatableTask) => {
    setShowDelete(true)
    setTaskToDelete({ type: 'repeatable', ...scheduler })
  }, [])

  const handleCreateListClose = useCallback(() => {
    setCreateListOpen(false)
  }, [setCreateListOpen])

  const [query, setQuery] = useState('')

  const handleQueryUpdate = (value: string) => setQuery(value.toLowerCase())

  useEffect(() => {
    if (tasks.length) {
      trackFeatureUsage(TrackedFeatures.Tasks)
    }
  }, [tasks.length])

  if (isFetching) {
    return <TaskPageSkeleton />
  }

  return (
    <Page
      title="Tasks"
      headerOptions={
        <div className="grid max-w-[500px] grid-cols-12 gap-[16px]">
          <SearchInput
            className="col-span-12 w-full flex-shrink-0 self-start lg:col-span-8"
            placeholder="Search"
            value={query}
            onInputChange={handleQueryUpdate}
          />
          <Button
            className="col-span-6 h-full gap-1 lg:col-span-4"
            onClick={() => {
              setTaskToEdit(undefined)
              setCreateTaskOpen(true)
            }}
          >
            <IconPlus className="h-5 w-5 flex-shrink-0" />
            New Task
          </Button>
        </div>
      }
    >
      <div className="flex mb-6 items-center overflow-hidden rounded-md border-l-4 px-4 py-2 bg-[#ffcc0042] border-[#ffcc00] dark:bg-[#ffcc001a]">
        <IconWarning className="h-5 w-5 text-secondary-600 mr-2 dark:text-[#ffcc00]" />
        <span className="text-secondary-600 text-sm font-medium dark:text-white">
          This page is no longer supported and will be removed on August 1st,
          2025. Please move your tasks to a different service.
        </span>
      </div>
      <TaskNavigation
        query={query}
        entries={entries}
        render={(filtered, activeKey) => {
          return (
            <div className="mt-6 overflow-x-auto">
              <TaskTable
                tasks={filtered}
                showType={activeKey === 'all'}
                showAssignedTo={activeKey === 'assigned'}
                onOpenDelete={handleShowDelete}
                onOpenEdit={handleShowEdit}
                onToggleCompleted={handleToggleCompleted}
              />
            </div>
          )
        }}
      />
      <div className="mt-8 overflow-x-scroll">
        <TaskSchedulerTable
          schedulers={repeatableTasks}
          onCreate={() => setCreateSchedulerOpen(true)}
          onDelete={handleDeleteScheduler}
          onEdit={handleShowEditScheduler}
        />
      </div>
      <div className="mt-8 overflow-x-scroll">
        <TaskListTable
          lists={lists}
          onCreate={() => setCreateListOpen(true)}
        />
      </div>
      <TaskEditorDialog
        task={taskToEdit}
        isOpen={isCreateTaskOpen}
        onVisibilityChange={setCreateTaskOpen}
      />
      <TaskSchedulerDialog
        isOpen={isCreateSchedulerOpen}
        onVisibilityChange={setCreateSchedulerOpen}
        scheduler={schedulerToEdit}
      />
      <CreateListDialog
        isOpen={isCreateListOpen}
        onClose={handleCreateListClose}
      />
      <DeleteTaskDialog
        task={taskToDelete}
        show={showDelete}
        onVisibilityChange={setShowDelete}
      />
    </Page>
  )
}
